import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSingleStudio, useUpdateStudio } from "@/hooks/useStudios";
import {
  useSingleStudioConfig,
  useLoadConfigToBridge,
  useConfigureBridgeSync,
} from "@/hooks/useStudioConfigs";
import { useBridgeStatus } from "@/hooks/useBridgeConnections";
import { useConfigData } from "@/hooks/useConfigData";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { QueryResult } from "@/components/QueryResult";
import { ConfigEditor } from "@/components/studio/ConfigEditor";
import type { StudioConfigData } from "@production-hub/shared";

const ConfigEditorPage = () => {
  const { studioId, configId } = useParams<{
    studioId: string;
    configId: string;
  }>();
  const navigate = useNavigate();

  const {
    data: studio,
    isLoading: studioLoading,
    isError: studioError,
    error: studioErr,
  } = useSingleStudio(studioId);
  const { data: config, isLoading: configLoading } =
    useSingleStudioConfig(configId);
  const { data: bridgeStatus, isError: bridgeError } = useBridgeStatus(
    studio?.bridge_url,
  );

  const bridgeOnline = !bridgeError && bridgeStatus?.status === "ok";
  const isActiveConfig = studio?.active_config_id === configId;

  const configData = useConfigData({
    configId: configId ?? "",
    studioId: studioId ?? "",
    bridgeUrl: studio?.bridge_url,
    bridgeOnline,
    isActiveConfig,
  });

  useSetCurrentEntity("studio", studioId, studio?.name);

  const headerTabs = useMemo(
    () => [
      {
        value: "devices",
        label: `Devices (${configData.connections.length})`,
      },
      { value: "layouts", label: `Layouts (${configData.layouts.length})` },
      { value: "profiles", label: `Profiles (${configData.profiles.length})` },
      {
        value: "wiring",
        label: `Wiring (${configData.physicalConnections.length})`,
      },
      { value: "dante", label: `Dante (${configData.dante.devices.length})` },
      { value: "history", label: "History" },
    ],
    [
      configData.connections.length,
      configData.layouts.length,
      configData.profiles.length,
      configData.physicalConnections.length,
      configData.dante.devices.length,
    ],
  );

  usePageHeader({
    title: config?.name,
    tabs: headerTabs,
    currentTab: "devices",
  });

  // Load to bridge action
  const loadToBridge = useLoadConfigToBridge();
  const configureBridgeSync = useConfigureBridgeSync();
  const updateStudio = useUpdateStudio();

  const handleLoadToBridge = async () => {
    if (!studio?.bridge_url || !config || !configId || !studioId) return;
    const cfgData = config.config_data as unknown as StudioConfigData;
    try {
      await loadToBridge.mutateAsync({
        bridgeUrl: studio.bridge_url,
        configData: cfgData,
      });
      if (studio.bridge_api_key) {
        await configureBridgeSync.mutateAsync({
          bridgeUrl: studio.bridge_url,
          apiUrl: window.location.origin,
          configId,
          apiKey: studio.bridge_api_key,
        });
      }
      await updateStudio.mutateAsync({
        id: studioId,
        studio: { active_config_id: configId },
      });
    } catch {
      // Error toasts handled by mutation hooks
    }
  };

  if (studioLoading || configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!studio || !config) {
    return (
      <QueryResult
        isError={studioError}
        error={studioErr}
        data={studio}
        entityName="studio configuration"
        backTo={studioId ? `/studios/${studioId}` : "/studios"}
        backLabel="Back to Studio"
      >
        {null}
      </QueryResult>
    );
  }

  return (
    <div className="space-y-6" data-testid="config-editor-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/studios/${studioId}?tab=configurations`)}
            data-testid="config-editor-back-button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1
                className="text-2xl font-bold"
                data-testid="config-editor-name"
              >
                {config.name}
              </h1>
              <Badge variant="outline" data-testid="config-version-badge">
                v{config.version}
              </Badge>
              {configData.mode === "online" ? (
                <Badge
                  variant="default"
                  className="gap-1 bg-green-600"
                  data-testid="config-mode-badge"
                >
                  <Wifi className="h-3 w-3" />
                  Live
                </Badge>
              ) : (
                <Badge
                  variant="secondary"
                  className="gap-1"
                  data-testid="config-mode-badge"
                >
                  <WifiOff className="h-3 w-3" />
                  Offline
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              {studio.name}
              {config.description && ` — ${config.description}`}
            </p>
          </div>
        </div>
      </div>

      {/* Load to bridge prompt when bridge is online but config isn't active */}
      {bridgeOnline && !isActiveConfig && (
        <Alert>
          <AlertDescription className="flex items-center justify-between">
            <span>
              Bridge is online but this config is not loaded. Load it to enable
              live editing with device controls.
            </span>
            <Button
              offlineEdit
              size="sm"
              onClick={handleLoadToBridge}
              disabled={loadToBridge.isPending}
            >
              Load to Bridge
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Config Editor */}
      <ConfigEditor
        configId={configId!}
        studioId={studioId!}
        configData={configData}
        bridgeUrl={studio.bridge_url ?? undefined}
      />
    </div>
  );
};

export default ConfigEditorPage;
